import { useState, useMemo, useCallback, useEffect } from 'react';
import type { Project, ProjectPhase } from '@/types';
import { listProjects as listProjectsApi, getErrorMessage } from '@/lib/api';

export type ProjectPhaseFilter = 'all' | ProjectPhase;
export type ProjectSortOption = 'votes' | 'recent' | 'alphabetical';

interface UseProjectsOptions {
  initialFilter?: ProjectPhaseFilter;
  initialSort?: ProjectSortOption;
  /** Limit number of projects to fetch (for featured section) */
  limit?: number;
}

interface UseProjectsReturn {
  projects: Project[];
  filter: ProjectPhaseFilter;
  setFilter: (filter: ProjectPhaseFilter) => void;
  sortBy: ProjectSortOption;
  setSortBy: (sort: ProjectSortOption) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  totalCount: number;
  filteredCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Map frontend sort options to API sort options
const sortOptionToApiSort = (sort: ProjectSortOption): 'trending' | 'newest' | 'most_voted' => {
  switch (sort) {
    case 'votes':
      return 'most_voted';
    case 'recent':
      return 'newest';
    case 'alphabetical':
      return 'most_voted'; // API doesn't support alphabetical, fallback to votes
    default:
      return 'trending';
  }
};

/**
 * Hook for managing project list with filtering, sorting, and search
 * Fetches projects from the API
 */
export const useProjects = (options: UseProjectsOptions = {}): UseProjectsReturn => {
  const {
    initialFilter = 'all',
    initialSort = 'votes',
    limit = 50,
  } = options;

  const [filter, setFilter] = useState<ProjectPhaseFilter>(initialFilter);
  const [sortBy, setSortBy] = useState<ProjectSortOption>(initialSort);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch projects from API
  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await listProjectsApi({
        phase: filter === 'all' ? undefined : filter,
        sort: sortOptionToApiSort(sortBy),
        limit,
      });

      setProjects(response.projects);
      setTotalCount(response.pagination.total);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [filter, sortBy, limit]);

  // Fetch on mount and when filter/sort changes
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Apply client-side search filtering and alphabetical sorting
  const filteredProjects = useMemo(() => {
    let result = [...projects];

    // Apply search filter (client-side)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        project =>
          project.title.toLowerCase().includes(query) ||
          project.description.toLowerCase().includes(query)
      );
    }

    // API doesn't support alphabetical, sort client-side
    if (sortBy === 'alphabetical') {
      result.sort((a, b) => a.title.localeCompare(b.title));
    }

    return result;
  }, [projects, sortBy, searchQuery]);

  const handleSetFilter = useCallback((newFilter: ProjectPhaseFilter) => {
    setFilter(newFilter);
  }, []);

  const handleSetSortBy = useCallback((newSort: ProjectSortOption) => {
    setSortBy(newSort);
  }, []);

  const handleSetSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return {
    projects: filteredProjects,
    filter,
    setFilter: handleSetFilter,
    sortBy,
    setSortBy: handleSetSortBy,
    searchQuery,
    setSearchQuery: handleSetSearchQuery,
    totalCount,
    filteredCount: filteredProjects.length,
    isLoading,
    error,
    refetch: fetchProjects,
  };
};

export default useProjects;
